import React, { useEffect, useRef, ReactNode, useState } from "react";
import { createPortal } from "react-dom";
import { ModalClose } from "@castlery/fortress";
import { cn } from "@/lib/utils";

export type DrawerDirection = "top" | "bottom" | "left" | "right";

export interface DrawerProps {
  /** 是否显示抽屉 */
  open: boolean;
  /** 抽屉关闭时的回调 */
  onClose: () => void;
  /** 抽屉内容 */
  children: ReactNode;
  /** 弹出方向 */
  direction?: DrawerDirection;
  /** 自定义容器，如果不指定则使用 document.body */
  container?: HTMLElement | null;
  /** 是否显示遮罩层 */
  showOverlay?: boolean;
  /** 点击遮罩层是否关闭抽屉 */
  closeOnOverlayClick?: boolean;
  /** 是否显示关闭按钮 */
  showCloseButton?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 遮罩层类名 */
  overlayClassName?: string;
  /** 抽屉内容类名 */
  contentClassName?: string;
  /** 抽屉宽度（左右方向时）或高度（上下方向时） */
  size?: string | number;
  /** 动画持续时间（毫秒） */
  duration?: number;
  /** 是否禁用动画 */
  disableAnimation?: boolean;
}

const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  direction = "bottom",
  container = null,
  showOverlay = true,
  closeOnOverlayClick = true,
  showCloseButton = false,
  className,
  overlayClassName,
  contentClassName,
  size,
  duration = 300,
  disableAnimation = false,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(open);
  const [shouldRender, setShouldRender] = useState(open);

  // 处理打开/关闭动画
  useEffect(() => {
    if (open) {
      setShouldRender(true);
      // 使用 requestAnimationFrame 确保 DOM 更新后再开始动画
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      // 动画结束后移除 DOM 元素
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [open, duration]);

  // 确保容器有正确的定位上下文
  useEffect(() => {
    if (container && open) {
      const originalPosition = container.style.position;
      const originalZIndex = container.style.zIndex;

      // 如果容器没有定位，设置为 relative
      if (!originalPosition || originalPosition === "static") {
        container.style.position = "relative";
      }

      // 确保容器有足够的 z-index
      if (!originalZIndex || parseInt(originalZIndex) < 50) {
        container.style.zIndex = "50";
      }

      return () => {
        // 恢复原始样式
        container.style.position = originalPosition;
        container.style.zIndex = originalZIndex;
      };
    }
  }, [container, open]);

  // 处理 ESC 键关闭
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      // 防止背景滚动
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [open, onClose]);

  // 处理遮罩层点击
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (closeOnOverlayClick && event.target === overlayRef.current) {
      onClose();
    }
  };

  // 获取抽屉样式
  const getDrawerStyles = () => {
    const baseStyles: React.CSSProperties = {
      transition: disableAnimation
        ? "none"
        : `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    };

    if (size) {
      if (direction === "left" || direction === "right") {
        baseStyles.width = typeof size === "number" ? `${size}px` : size;
      } else {
        baseStyles.height = typeof size === "number" ? `${size}px` : size;
      }
    }

    return baseStyles;
  };

  // 获取动画类名
  const getAnimationClasses = () => {
    if (disableAnimation) return "";

    const baseClasses = `transition-all ease-out`;

    // 初始状态：抽屉在隐藏位置
    const getInitialTransform = () => {
      switch (direction) {
        case "top":
          return "-translate-y-full";
        case "bottom":
          return "translate-y-full";
        case "left":
          return "-translate-x-full";
        case "right":
          return "translate-x-full";
        default:
          return "translate-y-full";
      }
    };

    // 最终状态：抽屉在显示位置
    const getFinalTransform = () => {
      switch (direction) {
        case "top":
        case "bottom":
          return "translate-y-0";
        case "left":
        case "right":
          return "translate-x-0";
        default:
          return "translate-y-0";
      }
    };

    return isVisible
      ? `${baseClasses} ${getFinalTransform()}`
      : `${baseClasses} ${getInitialTransform()}`;
  };

  // 获取位置类名
  const getPositionClasses = () => {
    // 如果指定了容器，使用绝对定位；否则使用固定定位
    const positionType = container ? "absolute" : "fixed";

    switch (direction) {
      case "top":
        return `${positionType} top-0 left-0 right-0`;
      case "bottom":
        return `${positionType} bottom-0 left-0 right-0`;
      case "left":
        return `${positionType} top-0 left-0 bottom-0`;
      case "right":
        return `${positionType} top-0 right-0 bottom-0`;
      default:
        return `${positionType} bottom-0 left-0 right-0`;
    }
  };

  // 获取尺寸类名
  const getSizeClasses = () => {
    if (size) return "";

    switch (direction) {
      case "top":
      case "bottom":
        return "h-auto max-h-[80%]";
      case "left":
      case "right":
        return "w-auto max-w-[80%]";
      default:
        return "h-auto max-h-[80%]";
    }
  };

  if (!shouldRender) return null;

  const drawerContent = (
    <div
      ref={overlayRef}
      className={cn(
        // 根据是否有容器决定定位方式
        container ? "absolute inset-0 z-50 flex" : "fixed inset-0 z-50 flex",
        {
          "items-start": direction === "top",
          "items-end": direction === "bottom",
          "justify-start": direction === "left",
          "justify-end": direction === "right",
        },
        overlayClassName
      )}
      onClick={handleOverlayClick}
    >
      {/* 遮罩层 */}
      {showOverlay && (
        <div
          className={cn(
            "absolute inset-0 bg-black/50",
            disableAnimation ? "" : "transition-opacity ease-out",
            isVisible ? "opacity-100" : "opacity-0"
          )}
          style={{
            transitionDuration: disableAnimation ? "0ms" : `${duration}ms`,
          }}
        />
      )}

      {/* 抽屉内容 */}
      <div
        ref={drawerRef}
        className={cn(
          "relative bg-[#FBF9F4] shadow-lg overflow-auto",
          getPositionClasses(),
          getSizeClasses(),
          getAnimationClasses(),
          className,
          contentClassName
        )}
        style={{
          ...getDrawerStyles(),
          transitionDuration: disableAnimation ? "0ms" : `${duration}ms`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 关闭按钮 */}
        {showCloseButton && (
          <ModalClose
            onClick={onClose}
            sx={{
              position: "absolute",
              top: "16px",
              right: "24px",
              width: "24px",
              height: "24px",
              zIndex: 10,
            }}
          />
        )}

        {/* 抽屉内容 */}
        <div>{children}</div>
      </div>
    </div>
  );

  // 渲染到指定容器或 document.body
  const targetContainer = container || document.body;
  return createPortal(drawerContent, targetContainer);
};

export default Drawer;
